import { compact, map, chain, get, first, includes, toLower, size, find, isEmpty, isEqual, sumBy } from "lodash"
import { createSelector } from "reselect"
import { getIsOpPro } from "modules/account-root/selectors"
import { getSelectedModelName } from "modules/portfolio-root/selectors/portfolios"
import { getPortfolioBetaInstrument } from "modules/portfolio-root/selectors/beta-instrument"
import { factorFormatter, assetDollarFormatter } from "helpers/formatters/numbers"
import { getBasePositionSet } from "selectors/base"
import { getNMVTooltipComponent } from "helpers/assets/tooltips"
import { METRIC_TYPES } from "helpers/assets/constants"
import { categoryTooltip, riskTooltip, factorTooltip } from "../helpers/tooltips"
import { NOTHELD_CONTRIBUTOR, percentageFormatter } from "../helpers"
import { getHistoricalFactorSensitivityUnavailable } from "./factorSensitivity/historical"
import { getIsIntraday, getIsActiveAndNotIntraday } from "./intraday"
import { getBetaFactors } from "./beta"
import { getSelectedCategory, getFactorsCategories } from "./categories"
import { getForecast } from "./forecast"
import { getOrderedFactors } from "./summary"
import { getSelectedContentSet } from "./exposure"
import { getGMV, getReferenceEquity } from "./positions"
import { getActionsBase, getDecompositionType, getIsModelFactorsUsed, getOrder, getSelectedMetricType } from "."
import { getBetaColumnsAndTooltips } from "helpers/tables"

const EMPTY_ARRAY = []

// Most metrics from marginal contribution are not aggregatable so the NOTHELD group created when going active should not
// return any values for these metrics as they would be wrong
function marginalPathAndGetter(path, canAggregate = false) {
  return {
    path,
    getter: (contributor) => {
      if (!canAggregate && (get(contributor, "id") === NOTHELD_CONTRIBUTOR.id || contributor.isAggregate)) {
        return null
      }
      return get(contributor, path)
    },
  }
}

// Standalone beta cannot be aggregated
function betaPathAndGetter(path, canAggregate = true) {
  return {
    path,
    getter: (contributor) => {
      if (!canAggregate && (get(contributor, "id") === NOTHELD_CONTRIBUTOR.id || contributor.isAggregate)) {
        return null
      }
      return get(contributor, path)
    },
  }
}

export const getFactorColumnFormat = createSelector(
  [getDecompositionType, getSelectedMetricType],
  (decompositionType, selectedMetricType) => {
    const isCurrencyExposure = selectedMetricType === METRIC_TYPES.CURRENCY_EXPOSURE
    let formatter
    if (isCurrencyExposure) {
      formatter = assetDollarFormatter
    } else if (includes(["risk", "predictedFactorSensitivity", "historicalFactorSensitivity"], decompositionType)) {
      formatter = percentageFormatter
    } else {
      formatter = factorFormatter
    }
    return { formatter, outputFormat: "number", isDollarColumn: isCurrencyExposure }
  },
)

export const getIsActionsBaseDifferentThanAnalyticsBase = createSelector(
  [getBasePositionSet, getActionsBase],
  (base, actionsBase) => base && actionsBase && !isEqual(base, actionsBase),
)

export const getFactorColumns = createSelector(
  [getSelectedCategory, getSelectedMetricType, getFactorsCategories, getOrderedFactors, getDecompositionType, getBetaFactors, getIsModelFactorsUsed, getIsOpPro, getIsActiveAndNotIntraday, getSelectedContentSet, getFactorColumnFormat, getHistoricalFactorSensitivityUnavailable, getPortfolioBetaInstrument, getSelectedModelName],
  (selectedCategory, selectedMetricType, categories, orderedFactors, decompositionType, betaFactors, isModelFactorsUsed, isOpPro, isActive, selectedContentSet, factorColumnFormat, historicalFactorSensitivityUnavailable, betaInstrument, modelName) => {
    // Marginal contribution screen does not use factor columns
    if (decompositionType === "marginal") {
      return EMPTY_ARRAY
    }

    // Risk has a special Factors category which are category groups
    if ((!selectedCategory || selectedCategory === "all") && decompositionType === "risk") {
      return [{
        category: "Risk Factors",
        getter: (security, path, categoryId) => chain(security)
          .get([path, "factors"])
          .filter(({ category: factorCategory }) => factorCategory === categoryId)
          // sumBy([]) will return 0, but we want to return NaN if there are no factors
          .thru(factors => isEmpty(factors) ? NaN : sumBy(factors, "value"))
          .value(),
        columns: map(categories, category => ({
          ...category,
          path: decompositionType,
          formatter: percentageFormatter,
          outputFormat: "number",
          tooltip: categoryTooltip(category, decompositionType, isActive, selectedContentSet),
          canAggregate: true,
        })),
      }]
    }
    if (decompositionType === "beta") {
      const betaColumnsAndTooltips = getBetaColumnsAndTooltips(modelName, betaInstrument)
      return [{
        category: "Beta",
        columns: map(betaFactors, (factor, betaType) => {
          const canAggregate = selectedMetricType !== METRIC_TYPES.STANDALONE
          const path = compact([
            "beta",
            !canAggregate && "standalone",
            betaType,
          ])
          return ({
            id: betaType,
            category: "Beta",
            ...factor,
            ...betaPathAndGetter(path, canAggregate),
            formatter: factorFormatter,
            outputFormat: "number",
            tooltip: factorTooltip(factor, decompositionType, isActive, selectedContentSet, betaType),
            tooltipContents: get(betaColumnsAndTooltips, [betaType === "dynamicPredicted" ? "custom" : betaType, "tooltipContents"]),
            canAggregate,
          })
        }),
      }]
    }

    const category = find(categories, ({ id }) => id === selectedCategory) ?? first(categories)
    const categoryGetter = contributor => chain(contributor)
      .get(["risk", "factors"])
      .filter(({ category: factorCategory }) => factorCategory === get(category, "id"))
      .sumBy("value")
      .value()

    const useMetricType = decompositionType === "exposure" || !isEmpty(selectedContentSet)
    const contributorAttribute = useMetricType ? selectedMetricType : "value"
    return compact([
      isModelFactorsUsed && !isOpPro && {
        ...category,
        id: selectedCategory || "all",
        getter: categoryGetter,
        outputFormat: "number",
        columns: [{
          label: get(category, "label"),
          id: get(category, "id"),
          getter: categoryGetter,
          formatter: percentageFormatter,
          outputFormat: "number",
          tooltip: categoryTooltip(category, "risk", isActive, selectedContentSet),
          canAggregate: true,
        }],
      },
      {
        id: decompositionType,
        category: "Factors",
        adjustedCategoryReason: decompositionType === "historicalFactorSensitivity" && historicalFactorSensitivityUnavailable ? historicalFactorSensitivityUnavailable : undefined,
        getter: (contributor, path, factorId) => get(contributor, [decompositionType, "factors", factorId, contributorAttribute]),
        columns: map(orderedFactors, (factor) => ({
          id: factor.label,
          ...factor,
          ...(category.color && { color: category.color }),
          getter: (contributor) => {
            return get(contributor, [decompositionType, "factors", factor.id, contributorAttribute])
          },
          ...factorColumnFormat,
          tooltip: factorTooltip(factor, decompositionType, isActive, selectedContentSet),
          canAggregate: true,
        })),
      },
    ])
  },
)

export const getForecastColumn = createSelector(
  [getDecompositionType, getForecast, getIsActiveAndNotIntraday, getIsOpPro, getIsIntraday],
  (decompositionType, forecast, isActive, isOpPro, isIntraday) => {
    // Marginal contribution screen does not use forecast column
    if (isIntraday || (isOpPro && !get(forecast, "forecast.id")) || decompositionType === "marginal") {
      return null
    }

    return {
      id: "forecast",
      category: "Forecast",
      label: forecast.forecast ? "Expected Returns" : "Implied Returns",
      path: "forecast.securityExpectedReturn",
      formatter: factorFormatter,
      getter: (contributor) => {
        const { isAggregate, isGroup } = contributor ?? {}
        if (isGroup) {
          return get(contributor, "forecast.totalExpectedReturn")
        }
        // When drilling down into a group, the aggregate row is essentially the group's contributor. That contributor will not be flagged
        // with isGroup but will have the totalExpectedReturn property with the pre-calculated total of weightedExpectedReturn
        if (isAggregate) {
          return get(contributor, "forecast.totalExpectedReturn") ?? get(contributor, "forecast.weightedExpectedReturn")
        }
        return get(contributor, "forecast.securityExpectedReturn")
      },
    }
  })

const getHideRiskColumns = createSelector(
  [getDecompositionType, getIsOpPro],
  (decompositionType, isOpPro) => isOpPro || decompositionType === "marginal", // getMarginalContributionColumns includes the few risk columns it needs
)

export const getRiskColumns = createSelector(
  [getIsActiveAndNotIntraday, getHideRiskColumns],
  (isActive, hideRiskColumns) => {
    if (hideRiskColumns) { return EMPTY_ARRAY }
    return [
      {
        id: "totalRisk",
        label: "Total risk contribution",
        path: "risk.summary.total",
        formatter: percentageFormatter,
        outputFormat: "percent",
        tooltip: riskTooltip("total", isActive),
        canAggregate: true,
      },
      {
        id: "specificRisk",
        label: "Specific",
        path: "risk.summary.specific",
        formatter: percentageFormatter,
        outputFormat: "percent",
        tooltip: riskTooltip("specific", isActive),
        canAggregate: true,
      },
      {
        id: "factorRisk",
        label: "Factor",
        path: "risk.summary.factors",
        formatter: percentageFormatter,
        outputFormat: "percent",
        tooltip: riskTooltip("factor", isActive),
        canAggregate: true,
      },
    ]
  },
)

export const getDescriptorsColumns = createSelector(
  [getIsActiveAndNotIntraday, getIsActionsBaseDifferentThanAnalyticsBase, getGMV, getReferenceEquity],
  (isActive, isDifferent, gmv, referenceEquity) => compact([
    {
      id: "economicExposure",
      label: "NMV",
      path: "economicExposure",
      formatter: assetDollarFormatter,
      isDollarColumn: true,
      outputFormat: "number",
      editable: true,
      isSticky: true,
      canAggregate: true,
      tooltipContents: getNMVTooltipComponent(gmv, referenceEquity),
    },
    {
      id: "positionPercentEquity",
      label: "% Equity",
      path: "positionPercentEquity",
      formatter: factorFormatter,
      outputFormat: "percent",
      editable: true,
      isSticky: true,
      canAggregate: true,
    },
    isActive && {
      id: "activePercentEquity",
      label: "Active % Equity",
      adjustedReason: isDifferent ? "The base used for rebalancing is different from the currently selected base so trading by the active percent equity has been disabled" : null,
      path: "activePercentEquity",
      formatter: factorFormatter,
      outputFormat: "percent",
      editable: !isDifferent,
      isSticky: true,
      canAggregate: true,
    },
  ]),
)

export const getMarginalContributionColumns = createSelector(
  [getDecompositionType, getIsActiveAndNotIntraday, getIsOpPro],
  (decompositionType, isActive, isOpPro) => {
    if (decompositionType !== "marginal" || isOpPro) { return EMPTY_ARRAY }
    return [{
      category: "Total Risk",
      getter: (security, path) => get(security, path),
      columns: [{
        id: "totalRisk",
        path: "risk.summary.total",
        label: isActive ? "Total % of active risk" : "Total % of risk",
        outputFormat: "percent",
        formatter: percentageFormatter,
      },
      {
        id: "totalRiskContribution",
        ...marginalPathAndGetter("marginal.total.riskContribution"),
        label: isActive ? "Active risk contribution" : "Risk contribution",
        outputFormat: "number",
        formatter: percentageFormatter,
        canAggregate: false,
        dowloadLabelPrefix: "Total",
      },
      {
        id: "totalRiskMarginalContribution",
        ...marginalPathAndGetter("marginal.total.marginalContribution"),
        label: "Marginal contribution",
        outputFormat: "number",
        formatter: factorFormatter,
        canAggregate: false,
        dowloadLabelPrefix: "Total",
      },
      {
        id: "totalRiskCorrelation",
        ...marginalPathAndGetter("marginal.total.correlation"),
        label: isActive ? "Relative correlation" : "Correlation",
        outputFormat: "number",
        formatter: factorFormatter,
        canAggregate: false,
      },
      {
        id: "totalRiskVolatility",
        ...marginalPathAndGetter("marginal.total.volatility"),
        label: isActive ? "Relative volatility" : "Volatility",
        outputFormat: "number",
        formatter: percentageFormatter,
        canAggregate: false,
      }],
    }, {
      category: "Specific Risk",
      getter: (security, path) => get(security, path),
      columns: [{
        id: "specificRisk",
        group: "Specific Risk",
        path: "risk.summary.specific",
        label: isActive ? "Specific % of active risk" : "Specific % of risk",
        outputFormat: "percent",
        formatter: percentageFormatter,
      },
      {
        id: "specificRiskContribution",
        ...marginalPathAndGetter("marginal.specific.riskContribution"),
        label: isActive ? "Active risk contribution" : "Risk contribution",
        outputFormat: "number",
        formatter: percentageFormatter,
        canAggregate: false,
        dowloadLabelPrefix: "Total",
      },
      {
        id: "specificRiskMarginalContribution",
        ...marginalPathAndGetter("marginal.specific.marginalContribution"),
        label: "Marginal Contribution",
        outputFormat: "number",
        formatter: factorFormatter,
        canAggregate: false,
        dowloadLabelPrefix: "Total",
      }],
    }, {
      category: "Factors Risk",
      getter: (security, path) => get(security, path),
      columns: [{
        id: "factorsRisk",
        category: "Factors Risk",
        path: "risk.summary.factors",
        label: isActive ? "Factors % of active risk" : "Factors % of risk",
        outputFormat: "percent",
        formatter: percentageFormatter,
      },
      {
        id: "factorsRiskContribution",
        category: "Factors Risk",
        ...marginalPathAndGetter("marginal.factors.riskContribution"),
        label: isActive ? "Active risk contribution" : "Risk contribution",
        outputFormat: "number",
        formatter: percentageFormatter,
        canAggregate: false,
        dowloadLabelPrefix: "Total",
      },
      {
        id: "factorsRiskMarginalContribution",
        category: "Factors Risk",
        ...marginalPathAndGetter("marginal.factors.marginalContribution"),
        label: "Marginal Contribution",
        outputFormat: "number",
        formatter: factorFormatter,
        canAggregate: false,
        dowloadLabelPrefix: "Total",
      }],
    }]
  },
)

function recursiveMapSort(columns, sort) {
  const { id, direction, absolute } = sort ?? {}
  return map(columns, column => {
    return {
      ...column,
      allowAbsSort: true,
      ...(id !== get(column, "id")
        ? { sorted: false }
        : {
          sorted: toLower(direction),
          absSorted: absolute,
        }),
      ...(size(get(column, "columns")) && { columns: recursiveMapSort(column.columns, sort) }),
    }
  })
}

export const getColumnsByCategory = createSelector(
  [getDecompositionType, getDescriptorsColumns, getRiskColumns, getFactorColumns, getForecastColumn, getMarginalContributionColumns, getOrder],
  (decompositionType, descriptorsColumns, riskColumns, factorColumns, forecastColumn, marginalContributionColumns, sort) => {
    const metricColumns = recursiveMapSort(decompositionType === "marginal" ? marginalContributionColumns : factorColumns, sort)
    const result = chain([
      {
        category: "Descriptors",
        getter: (security, path) => get(security, path),
        columns: recursiveMapSort(descriptorsColumns, sort),
      },
      decompositionType !== "marginal" && !isEmpty(forecastColumn) && {
        category: "Forecast",
        getter: (security, path) => get(security, path),
        columns: recursiveMapSort([forecastColumn], sort),
      },
      decompositionType !== "marginal" && !isEmpty(riskColumns) && {
        category: "Risk",
        getter: (security, path) => get(security, path),
        columns: recursiveMapSort(riskColumns, sort),
      },
      ...metricColumns,
    ])
      .compact()
      .value()
    return result
  },
)

export const getAllColumns = createSelector(
  [getColumnsByCategory],
  (columnsByCategory) => chain(columnsByCategory)
    .map(columnCategory => map(columnCategory.columns, column => ({
      ...column,
      getter: columnCategory.getter,
    })))
    .flatten()
    .value(),
)
